// Console search engine (system console-search): the ranking behind the
// nav-bar search field. Pure — items in, ranked hits out — so it is unit
// tested with `node --test` and the component only wires sources and keys.
//
// Items: { id, group, label, aliases?: [], hint?, keywords?, to? | action?,
//          updatedAt?, prior? }  — `prior` is a small kind bias (modules >
// apps > workflows > the rest) the caller sets per source.
//
// Scoring, per query token, best match across fields (label 3, aliases 2.5,
// hint 1.5, group 1, keywords 0.5):
//   exact word 1.0 > word prefix 0.85 > acronym 0.7 > substring 0.5 > fuzzy 0.4
// (no fuzzy on keywords — long free text would turn noise into hits).
// Every token must land somewhere (AND semantics) or the item drops. On top:
// a whole-query label-prefix bonus, a shorter-label tiebreak, the person's
// own history (log1p(count) decayed over 14 days), freshness, and the kind
// prior. Results are diversified: at most `perGroup` rows per group before
// the rest fills in.

const FIELD_WEIGHTS = { label: 3, aliases: 2.5, hint: 1.5, group: 1, keywords: 0.5 };
const HALF_LIFE_DAYS = 14;
const DAY = 86400000;

export function normalize(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function tokenize(s) {
  return normalize(s).split(/[^a-z0-9]+/).filter(Boolean);
}

function field(text) {
  const norm = normalize(text);
  const words = tokenize(norm);
  return { text: norm, words, initials: words.map((w) => w[0]).join('') };
}

export function buildIndex(items) {
  return (items || []).map((item) => ({
    item,
    label: field(item.label),
    aliases: field((item.aliases || []).join(' ')),
    hint: field(item.hint),
    group: field(item.group),
    keywords: field(item.keywords),
  }));
}

// Damerau-Levenshtein (optimal string alignment), capped: returns max+1 as
// soon as the distance cannot come back under `max`.
export function editDistance(a, b, max = 2) {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const prev2 = [];
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  let cur = new Array(b.length + 1);
  for (let i = 1; i <= a.length; i += 1) {
    cur[0] = i;
    let rowMin = cur[0];
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, prev2[j - 2] + 1);
      cur[j] = v;
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > max) return max + 1;
    prev2.splice(0, prev2.length, ...prev);
    [prev, cur] = [cur, prev];
  }
  return prev[b.length];
}

function fuzzyBudget(token) {
  if (token.length >= 8) return 2;
  if (token.length >= 4) return 1;
  return 0;
}

// Best match of one token against one field. Returns { q, range } where q
// is the match quality and range the highlighted span in field.text (or
// null when nothing matched).
function matchField(token, f, fuzzy = true) {
  if (!f.words.length) return null;
  let best = null;
  const keep = (q, range) => { if (!best || q > best.q) best = { q, range }; };
  let pos = 0;
  for (const w of f.words) {
    const at = f.text.indexOf(w, pos);
    pos = at + w.length;
    if (w === token) { keep(1, [at, at + w.length]); break; }
    if (w.startsWith(token)) keep(0.85, [at, at + token.length]);
    else if (w.includes(token)) keep(0.5, [at + w.indexOf(token), at + w.indexOf(token) + token.length]);
  }
  if (best && best.q >= 0.85) return best;
  if (token.length >= 2 && f.words.length >= 2 && f.initials.startsWith(token)) {
    const range = [];
    let p = 0;
    for (let i = 0; i < token.length; i += 1) {
      const at = f.text.indexOf(f.words[i], p);
      range.push([at, at + 1]);
      p = at + f.words[i].length;
    }
    keep(0.7, range);
  }
  if (best) return best;
  const budget = fuzzy ? fuzzyBudget(token) : 0;
  if (budget) {
    let p = 0;
    for (const w of f.words) {
      const at = f.text.indexOf(w, p);
      p = at + w.length;
      if (w.length < 4) continue;
      const cands = [w];
      if (w.length > token.length) cands.push(w.slice(0, token.length), w.slice(0, token.length + 1));
      let d = budget + 1;
      for (const c of cands) d = Math.min(d, editDistance(token, c, budget));
      if (d <= budget) { keep(0.4 - 0.05 * d, [at, at + w.length]); }
    }
  }
  return best;
}

// Timestamps arrive as numbers, Dates or Firestore Timestamps; anything
// else counts as "never" so a stray value can never NaN the score.
function millis(at) {
  if (typeof at === 'number') return Number.isFinite(at) ? at : 0;
  if (at instanceof Date) return at.getTime();
  if (at && typeof at.toMillis === 'function') return at.toMillis();
  if (at && typeof at.seconds === 'number') return at.seconds * 1000;
  return 0;
}

function decay(at, now) {
  const t = millis(at);
  if (!t) return 0;
  const age = Math.max(0, now - t) / DAY;
  return Math.pow(0.5, age / HALF_LIFE_DAYS);
}

export function historyBoost(entry, now = Date.now()) {
  if (!entry || !entry.n) return 0;
  return Math.log1p(entry.n) * decay(entry.at, now);
}

function mergeRanges(ranges) {
  const flat = ranges.flat().filter((r) => Array.isArray(r) && r.length === 2 && r[0] < r[1]);
  flat.sort((a, b) => a[0] - b[0]);
  const out = [];
  for (const r of flat) {
    const last = out[out.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else out.push([r[0], r[1]]);
  }
  return out;
}

function scoreEntry(entry, tokens, query, history, now) {
  let score = 0;
  const labelRanges = [];
  for (const t of tokens) {
    let best = 0;
    let bestRange = null;
    let bestIsLabel = false;
    for (const name of Object.keys(FIELD_WEIGHTS)) {
      // Keywords are long free text (a module's purpose, a doc's opening):
      // a typo-tolerant match there would make noise look like a hit.
      const m = matchField(t, entry[name], name !== 'keywords');
      if (!m) continue;
      const s = m.q * FIELD_WEIGHTS[name];
      if (s > best) { best = s; bestRange = m.range; bestIsLabel = name === 'label'; }
    }
    if (!best) return null;
    score += best;
    if (bestIsLabel && bestRange) labelRanges.push(Array.isArray(bestRange[0]) ? bestRange : [bestRange]);
    else {
      // Still highlight the token where it appears in the label, if it does.
      const m = matchField(t, entry.label);
      if (m && m.q >= 0.5) labelRanges.push(Array.isArray(m.range[0]) ? m.range : [m.range]);
    }
  }
  if (entry.label.text.startsWith(query)) score += 1;
  else if (entry.label.text === tokens.join(' ')) score += 1;
  score -= Math.min(0.3, entry.label.words.length * 0.01);
  score += historyBoost(history[entry.item.id], now);
  score += 0.05 * decay(entry.item.updatedAt, now);
  score += entry.item.prior || 0;
  const ranges = entry.label.text.length === String(entry.item.label || '').length ? mergeRanges(labelRanges) : [];
  return { item: entry.item, score, ranges };
}

function diversify(hits, limit, perGroup) {
  const out = [];
  const count = {};
  const rest = [];
  for (const h of hits) {
    const g = h.item.group || '';
    if (out.length < limit && (count[g] || 0) < perGroup) { out.push(h); count[g] = (count[g] || 0) + 1; } else rest.push(h);
  }
  for (const h of rest) { if (out.length >= limit) break; out.push(h); }
  return out;
}

export function recentHits(index, history, { limit = 5, now = Date.now() } = {}) {
  const byId = new Map(index.map((e) => [e.item.id, e.item]));
  return Object.entries(history || {})
    .map(([id, h]) => ({ item: byId.get(id), score: historyBoost(h, now), at: h.at }))
    .filter((h) => h.item && h.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((h) => ({ item: h.item, score: h.score, ranges: [], recent: true }));
}

// Empty query: what this person opens, then the kinds with the highest
// prior (modules, help). Non-empty: scored + diversified hits.
export function rank(index, query, { history = {}, limit = 12, perGroup = 5, now = Date.now() } = {}) {
  const q = normalize(query).trim();
  const tokens = tokenize(q);
  if (!tokens.length) {
    const recents = recentHits(index, history, { now });
    const seen = new Set(recents.map((h) => h.item.id));
    const defaults = index
      .filter((e) => !seen.has(e.item.id) && (e.item.prior || 0) > 0)
      .sort((a, b) => (b.item.prior || 0) - (a.item.prior || 0))
      .map((e) => ({ item: e.item, score: e.item.prior || 0, ranges: [] }));
    return [...recents, ...defaults].slice(0, limit);
  }
  const hits = [];
  for (const entry of index) {
    const h = scoreEntry(entry, tokens, q, history, now);
    if (h) hits.push(h);
  }
  hits.sort((a, b) => b.score - a.score || String(a.item.label).localeCompare(String(b.item.label)));
  return diversify(hits, limit, perGroup);
}

// History: { [itemId]: { n, at } }, capped so localStorage stays small.
export function recordPick(history, id, { now = Date.now(), max = 50 } = {}) {
  const prev = (history || {})[id];
  const next = { ...(history || {}), [id]: { n: (prev?.n || 0) + 1, at: now } };
  const ids = Object.keys(next);
  if (ids.length > max) {
    ids.sort((a, b) => historyBoost(next[a], now) - historyBoost(next[b], now));
    for (const drop of ids.slice(0, ids.length - max)) delete next[drop];
  }
  return next;
}

// Splits a label into [{ text, hit }] pieces for <mark> rendering.
export function highlight(label, ranges) {
  const s = String(label || '');
  if (!ranges || !ranges.length) return [{ text: s, hit: false }];
  const out = [];
  let pos = 0;
  for (const [a, b] of ranges) {
    if (a > pos) out.push({ text: s.slice(pos, a), hit: false });
    out.push({ text: s.slice(a, b), hit: true });
    pos = b;
  }
  if (pos < s.length) out.push({ text: s.slice(pos), hit: false });
  return out;
}
